import { useCallback, useEffect, useRef, useState } from "react";
import { calculateScale } from "../utils/viewport";
import { useViewTransform } from "../../LiveDashboardModal/hooks/useViewTransform";
import { useMapLayers } from "../../LiveDashboardModal/components/LayerControls";

/**
 * The plan editor's two modes and its zoom/pan view (plane sites only).
 *
 *   瀏覽 (browse, the default): wheel zoom, drag pan and the zoom buttons;
 *     nothing on the plan moves, places or draws.
 *   編輯模式: the view holds still where the operator left it (gestures off,
 *     NOT reset) and every editing gesture works as it always has. It is the
 *     parent of the existing sub-modes (可行區域, 越線, symbol placing, batch):
 *     their toggles call `enterEditMode`, and leaving calls `onLeave`, which
 *     the host uses to cancel whichever of them is active.
 *
 * Line and tunnel sites have no modes and no zoom: `editing` is always true
 * there and the view stays at identity, exactly as before.
 *
 * The view lives HERE, above the canvas, because the host's pixel->metre
 * handlers need the same zoom/pan the canvas draws with
 * (useCoordinateConversion takes `view`).
 */
export const useEditorMode = ({
  open,
  machineID,
  floorPlan,
  canvasSize,
  onLeave,
}) => {
  const { dimensions, layoutType } = floorPlan;
  const [editMode, setEditMode] = useState(false);
  // The canvas element, as state: useViewTransform attaches its wheel
  // listener when `enabled` turns on, so `enabled` must wait for the element
  // to exist (the modal renders its body only once it first opens).
  const [canvasEl, setCanvasEl] = useState(null);
  const onLeaveRef = useRef(onLeave);
  onLeaveRef.current = onLeave;

  const modesShown = layoutType === "plane";
  const layers = useMapLayers({ siteId: machineID, ...floorPlan });
  const zoomEnabled =
    !!open &&
    modesShown &&
    !!canvasEl &&
    calculateScale(dimensions, canvasSize, layoutType).isValid;

  const view = useViewTransform({
    dimensions,
    canvasSize,
    layoutType,
    enabled: zoomEnabled,
    gesturesEnabled: !editMode,
    // Every open starts from fit-to-floor.
    resetKey: `${machineID}:${!!open}`,
  });

  const { containerRef } = view;
  const attachCanvas = useCallback(
    (el) => {
      containerRef.current = el;
      setCanvasEl(el);
    },
    [containerRef],
  );

  const leaveEditMode = () => {
    setEditMode(false);
    if (onLeaveRef.current) onLeaveRef.current();
  };

  // Closing the modal leaves 編輯模式 too, so every open starts in 瀏覽.
  useEffect(() => {
    if (open) return;
    setEditMode(false);
    if (onLeaveRef.current) onLeaveRef.current();
  }, [open]);

  return {
    modesShown,
    editMode,
    editing: !modesShown || editMode,
    enterEditMode: () => setEditMode(true),
    toggleEditMode: () => (editMode ? leaveEditMode() : setEditMode(true)),
    zoomEnabled,
    view,
    layers,
    attachCanvas,
  };
};

export default useEditorMode;
